import { memo, useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { ChartWrapper } from '../shared/ChartWrapper';
import { TRAFFIC_HEX } from '../shared/constants';
import { useOverviewData } from './useOverviewData';

const OUTPUT_TYPES = [
  { name: 'Knowledge Products', key: 'Knowledge product', color: '#b45309' },
  { name: 'Capacity Sharing', key: 'Capacity sharing for development', color: '#a16207' },
  { name: 'Innovation Dev', key: 'Innovation development', color: '#92400e' },
  { name: 'Other Output', key: 'Other output', color: '#854d0e' },
];
const OUTCOME_TYPES = [
  { name: 'Innovation Use', key: 'Innovation use', color: '#be123c' },
  { name: 'Policy Change', key: 'Policy change', color: '#9f1239' },
  { name: 'Other Outcome', key: 'Other outcome', color: '#881337' },
  { name: 'Capacity Change', key: 'Capacity change', color: '#a21caf' },
  { name: 'Innovation Packages', key: 'Innovation Package', color: '#7e22ce' },
];
const IMPACT_TYPES = [
  { name: 'Impact Contributions', key: 'Impact contribution', color: '#7e22ce' },
];

const IA_CONFIG = [
  { key: 'gender', label: 'Gender', color: '#8B5CF6' },
  { key: 'climate', label: 'Climate', color: '#006837' },
  { key: 'nutrition', label: 'Nutrition', color: '#F37021' },
  { key: 'biodiversity', label: 'Biodiversity', color: '#14B8A6' },
  { key: 'poverty', label: 'Poverty', color: '#007DBC' },
];

const TRAFFIC_COLORS = TRAFFIC_HEX;

const W = 800;
const H = 420;
const COL_W = 140;
const COL_GAP = 80;
const COL_X = [60, 60 + COL_W + COL_GAP, 60 + 2 * (COL_W + COL_GAP)];
const TOP_Y = 50;
const BAR_GAP = 4;

function bezierPath(x1, y1, h1, x2, y2, h2) {
  const mx = (x1 + x2) / 2;
  return `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2} L${x2},${y2 + h2} C${mx},${y2 + h2} ${mx},${y1 + h1} ${x1},${y1 + h1} Z`;
}

export const ExecutiveSankeyDiagram = memo(function ExecutiveSankeyDiagram() {
  const { stats, byTypeAndFunding, byImpactArea, areasOfWork, filteredResults } = useOverviewData();
  const [tooltip, setTooltip] = useState(null);
  const [hovered, setHovered] = useState(null); // { type: 'bar', key } | { type: 'flow', index }
  const [animated, setAnimated] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setAnimated(true), 50);
    return () => clearTimeout(t);
  }, []);

  const byType = stats?.byType;

  const buildColumn = (types) =>
    types.map(t => {
      const funding = byTypeAndFunding?.[t.key] || { W1W2: 0, W3Bilateral: 0, total: 0 };
      return { ...t, count: byType?.[t.key] || 0, w1w2: funding.W1W2, w3: funding.W3Bilateral };
    }).filter(t => t.count > 0);

  const outputItems = useMemo(() => buildColumn(OUTPUT_TYPES), [byType]);
  const outcomeItems = useMemo(() => buildColumn(OUTCOME_TYPES), [byType]);
  const impactItems = useMemo(() => buildColumn(IMPACT_TYPES), [byType]);

  const totalOutputs = outputItems.reduce((s, t) => s + t.count, 0);
  const totalOutcomes = outcomeItems.reduce((s, t) => s + t.count, 0);
  const totalImpact = impactItems.reduce((s, t) => s + t.count, 0);
  const AVAILABLE_H = H - TOP_Y - 40;
  const MIN_BAR_H = 18; // minimum bar height so small items stay hoverable

  // Square-root scale: each column gets height proportional to sqrt(total),
  // so smaller columns aren't dwarfed by the largest one.
  const sqrtMax = Math.sqrt(Math.max(totalOutputs, totalOutcomes, totalImpact, 1));

  const layoutColumn = (items, total) => {
    const colScale = total > 0 ? (Math.sqrt(total) / sqrtMax) * AVAILABLE_H / total : 0;
    const colH = items.reduce((s, item) => s + Math.max(item.count * colScale, MIN_BAR_H) + BAR_GAP, -BAR_GAP);
    let y = TOP_Y + (AVAILABLE_H - colH) / 2;
    return items.map(item => {
      const h = Math.max(item.count * colScale, MIN_BAR_H);
      const positioned = { ...item, y, h };
      y += h + BAR_GAP;
      return positioned;
    });
  };

  const outputBars = useMemo(() => layoutColumn(outputItems, totalOutputs), [outputItems, totalOutputs, sqrtMax]);
  const outcomeBars = useMemo(() => layoutColumn(outcomeItems, totalOutcomes), [outcomeItems, totalOutcomes, sqrtMax]);
  const impactBars = useMemo(() => layoutColumn(impactItems, totalImpact), [impactItems, totalImpact, sqrtMax]);

  // Flow bands: proportional from each output to outcomes pool, and outcomes to impact pool
  const flows = useMemo(() => {
    const paths = [];
    let idx = 0;
    // Outputs -> Outcomes (each output flows proportionally to each outcome)
    const oStartX = COL_X[0] + COL_W;
    const oEndX = COL_X[1];
    for (const ob of outputBars) {
      let srcY = ob.y;
      for (const oc of outcomeBars) {
        const fraction = oc.count / (totalOutcomes || 1);
        const bandH = Math.max(ob.h * fraction, 0.5);
        const dstFraction = ob.count / (totalOutputs || 1);
        const dstH = Math.max(oc.h * dstFraction, 0.5);
        const dstY = oc.y + oc.h * (outputBars.indexOf(ob) / outputBars.length);
        paths.push({
          d: bezierPath(oStartX, srcY, bandH, oEndX, dstY, dstH),
          color: ob.color,
          srcKey: ob.key, dstKey: oc.key,
          srcName: ob.name, dstName: oc.name,
          index: idx++,
        });
        srcY += bandH;
      }
    }
    // Outcomes -> Impact
    const o2StartX = COL_X[1] + COL_W;
    const o2EndX = COL_X[2];
    for (const oc of outcomeBars) {
      for (const ip of impactBars) {
        const fraction = oc.count / (totalOutcomes || 1);
        const bandH = Math.max(oc.h, 0.5);
        const dstH = Math.max(ip.h * fraction, 0.5);
        const dstY = ip.y + ip.h * (outcomeBars.indexOf(oc) / outcomeBars.length);
        paths.push({
          d: bezierPath(o2StartX, oc.y, bandH, o2EndX, dstY, dstH),
          color: oc.color,
          srcKey: oc.key, dstKey: ip.key,
          srcName: oc.name, dstName: ip.name,
          index: idx++,
        });
      }
    }
    return paths;
  }, [outputBars, outcomeBars, impactBars, totalOutputs, totalOutcomes]);

  // Highlight helpers
  const isFlowHighlighted = useCallback((flow) => {
    if (!hovered) return false;
    if (hovered.type === 'flow') return hovered.index === flow.index;
    if (hovered.type === 'bar') return flow.srcKey === hovered.key || flow.dstKey === hovered.key;
    return false;
  }, [hovered]);

  const isBarConnected = useCallback((barKey) => {
    if (!hovered) return false;
    if (hovered.type === 'bar') return hovered.key === barKey;
    if (hovered.type === 'flow') {
      const f = flows[hovered.index];
      return f && (f.srcKey === barKey || f.dstKey === barKey);
    }
    return false;
  }, [hovered, flows]);

  const iaData = useMemo(() => {
    return IA_CONFIG.map(ia => {
      const counts = byImpactArea?.[ia.key] || { 0: 0, 1: 0, 2: 0 };
      const principal = counts[2] || 0;
      const significant = counts[1] || 0;
      const notTargeted = counts[0] || 0;
      const total = principal + significant + notTargeted;
      return { ...ia, principal, significant, notTargeted, total };
    });
  }, [byImpactArea]);

  const fmt = n => n.toLocaleString();

  const showTooltip = useCallback((info, e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top - 10, ...info });
  }, []);

  const chartData = useMemo(() => {
    if (!stats) return [];
    return [
      ...outputItems.map(t => ({ sphere: 'Outputs', type: t.name, count: t.count, w1w2: t.w1w2, w3_bilateral: t.w3 })),
      ...outcomeItems.map(t => ({ sphere: 'Outcomes', type: t.name, count: t.count, w1w2: t.w1w2, w3_bilateral: t.w3 })),
      ...impactItems.map(t => ({ sphere: 'Impact', type: t.name, count: t.count, w1w2: t.w1w2, w3_bilateral: t.w3 })),
    ];
  }, [stats, outputItems, outcomeItems, impactItems]);

  if (!stats) return null;

  const isAnythingHovered = hovered !== null;

  const ColumnHeader = ({ x, label, subtitle, total, color }) => (
    <g>
      <text x={x + COL_W / 2} y={TOP_Y - 22} textAnchor="middle" fontSize="10" fontWeight="700" fill={color} style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </text>
      <text x={x + COL_W / 2} y={TOP_Y - 10} textAnchor="middle" fontSize="9" fill="var(--text-tertiary, #9ca3af)">
        {subtitle} ({fmt(total)})
      </text>
    </g>
  );

  const Bar = ({ item, x }) => {
    const fitsLabel = item.h > 16;
    const w1w2Frac = item.count > 0 ? item.w1w2 / item.count : 1;
    const w1w2H = Math.max(item.h * w1w2Frac, 0);
    const w3H = item.h - w1w2H;
    const barDimmed = isAnythingHovered && !isBarConnected(item.key);
    const baseOpacity = barDimmed ? 0.25 : 0.85;
    const animStyle = {
      opacity: animated ? baseOpacity : 0,
      transition: animated ? 'opacity 0.2s' : `opacity 0.6s ease ${0.1 + outputBars.indexOf(item) * 0.05}s`,
    };
    return (
      <g
        onMouseEnter={e => { setHovered({ type: 'bar', key: item.key }); showTooltip({ item }, e); }}
        onMouseMove={e => showTooltip({ item }, e)}
        onMouseLeave={() => { setHovered(null); setTooltip(null); }}
        style={{ cursor: 'pointer' }}
      >
        {/* W1/W2 segment (top, solid) */}
        {w1w2H > 0 && (
          <rect
            x={x} y={item.y} width={COL_W} height={w1w2H}
            rx={w3H > 0 ? 0 : 4} ry={w3H > 0 ? 0 : 4}
            fill={item.color} style={animStyle}
          />
        )}
        {/* W3/Bilateral segment (bottom, diagonal stripe pattern) */}
        {w3H > 0 && (
          <rect
            x={x} y={item.y + w1w2H} width={COL_W} height={w3H}
            rx={w1w2H > 0 ? 0 : 4} ry={w1w2H > 0 ? 0 : 4}
            fill={`url(#stripe-${item.key.replace(/\s+/g, '-').toLowerCase()})`} style={animStyle}
          />
        )}
        {/* Rounded corners clip — full bar outline */}
        <rect
          x={x} y={item.y} width={COL_W} height={item.h}
          rx={4} fill="none" stroke={item.color} strokeWidth="0.5"
          style={{ ...animStyle, opacity: animated ? (barDimmed ? 0.1 : 0.3) : 0 }}
        />
        {fitsLabel ? (
          <>
            <text x={x + COL_W / 2} y={item.y + item.h / 2 + 4} textAnchor="middle" fontSize="11" fontWeight="600" fill="#fff"
              style={{ opacity: animated ? (barDimmed ? 0.4 : 1) : 0, transition: 'opacity 0.2s' }}>
              {item.name}
            </text>
            <text x={x + COL_W + 4} y={item.y + item.h / 2 + 4} fontSize="10" fill="var(--text-tertiary, #9ca3af)"
              style={{ opacity: barDimmed ? 0.3 : 1, transition: 'opacity 0.2s' }}>
              {fmt(item.count)}
            </text>
          </>
        ) : (
          <text x={x + COL_W + 4} y={item.y + item.h / 2 + 4} fontSize="10" fill="var(--text-secondary, #6b7280)"
            style={{ opacity: barDimmed ? 0.3 : 1, transition: 'opacity 0.2s' }}>
            <tspan fontWeight="600">{item.name}</tspan>
            <tspan dx="5" fill="var(--text-tertiary, #9ca3af)">{fmt(item.count)}</tspan>
          </text>
        )}
      </g>
    );
  };

  return (
    <ChartWrapper
      title="Executive Summary — Pathway Flow"
      info="Left-to-right flow showing how outputs (Sphere of Control) feed into outcomes (Sphere of Influence) and impact (Sphere of Interest). Flows are proportional to result counts. Below: Impact Area targeting heatmap and AoW progress."
      chartData={chartData}
      sourceResults={filteredResults}
      exportFilename="executive-sankey"
    >
      <div ref={containerRef} className="relative" onMouseLeave={() => { setHovered(null); setTooltip(null); }}>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ overflow: 'visible' }}>
          {/* Pattern defs for W3/Bilateral striped segments + arrow marker */}
          <defs>
            {[...OUTPUT_TYPES, ...OUTCOME_TYPES, ...IMPACT_TYPES].map(t => {
              const id = t.key.replace(/\s+/g, '-').toLowerCase();
              return (
                <pattern key={id} id={`stripe-${id}`} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                  <rect width="6" height="6" fill={t.color} opacity="0.4" />
                  <line x1="0" y1="0" x2="0" y2="6" stroke={t.color} strokeWidth="2" opacity="0.7" />
                </pattern>
              );
            })}
            <marker id="sankey-arrow" viewBox="0 0 6 6" refX="5" refY="3" markerWidth="6" markerHeight="6" orient="auto">
              <path d="M0,0 L6,3 L0,6 Z" fill="var(--text-tertiary, #9ca3af)" opacity="0.4" />
            </marker>
          </defs>

          {/* Column headers */}
          <ColumnHeader x={COL_X[0]} label="Sphere of Control" subtitle="Outputs" total={totalOutputs} color="#92400e" />
          <ColumnHeader x={COL_X[1]} label="Sphere of Influence" subtitle="Outcomes" total={totalOutcomes} color="#9f1239" />
          <ColumnHeader x={COL_X[2]} label="Sphere of Interest" subtitle="Impact" total={totalImpact} color="#6b21a8" />

          {/* Flow bands */}
          {flows.map((f) => {
            const hi = isFlowHighlighted(f);
            const dimmed = isAnythingHovered && !hi;
            return (
              <path
                key={f.index} d={f.d} fill={f.color}
                style={{
                  opacity: animated ? (hi ? 0.4 : dimmed ? 0.03 : 0.12) : 0,
                  transition: animated ? 'opacity 0.2s' : `opacity 0.8s ease ${0.3 + f.index * 0.02}s`,
                  cursor: 'pointer',
                }}
                onMouseEnter={e => {
                  setHovered({ type: 'flow', index: f.index });
                  showTooltip({ flow: f }, e);
                }}
                onMouseMove={e => showTooltip({ flow: f }, e)}
                onMouseLeave={() => { setHovered(null); setTooltip(null); }}
              />
            );
          })}

          {/* Bars */}
          {outputBars.map(item => <Bar key={item.key} item={item} x={COL_X[0]} />)}
          {outcomeBars.map(item => <Bar key={item.key} item={item} x={COL_X[1]} />)}
          {impactBars.map(item => <Bar key={item.key} item={item} x={COL_X[2]} />)}

          <line x1={COL_X[0] + COL_W + 5} y1={H / 2} x2={COL_X[1] - 5} y2={H / 2} stroke="var(--text-tertiary, #9ca3af)" strokeWidth="1" strokeDasharray="4,3" markerEnd="url(#sankey-arrow)" opacity="0.4" />
          <line x1={COL_X[1] + COL_W + 5} y1={H / 2} x2={COL_X[2] - 5} y2={H / 2} stroke="var(--text-tertiary, #9ca3af)" strokeWidth="1" strokeDasharray="4,3" markerEnd="url(#sankey-arrow)" opacity="0.4" />
        </svg>

        {/* Tooltip */}
        {tooltip && tooltip.item && (
          <div
            className="absolute bg-gray-900 text-white text-xs rounded-lg px-3 py-2 pointer-events-none shadow-lg z-50"
            style={{ left: tooltip.x, top: tooltip.y, transform: 'translate(-50%, -100%)' }}
          >
            <div className="font-semibold">{tooltip.item.name}</div>
            <div className="text-gray-400">{fmt(tooltip.item.count)} results</div>
            {(tooltip.item.w1w2 > 0 || tooltip.item.w3 > 0) && (
              <div className="text-gray-400 mt-0.5">
                <span>{fmt(tooltip.item.w1w2)} W1/W2</span>
                <span className="mx-1">+</span>
                <span>{fmt(tooltip.item.w3)} W3/Bilateral</span>
              </div>
            )}
          </div>
        )}
        {tooltip && tooltip.flow && (
          <div
            className="absolute bg-gray-900 text-white text-xs rounded-lg px-3 py-2 pointer-events-none shadow-lg z-50"
            style={{ left: tooltip.x, top: tooltip.y, transform: 'translate(-50%, -100%)' }}
          >
            <div className="font-semibold" style={{ color: tooltip.flow.color }}>{tooltip.flow.srcName}</div>
            <div className="text-gray-400">&#8594; {tooltip.flow.dstName}</div>
          </div>
        )}
      </div>

      {/* Funding type legend */}
      <div className="flex items-center justify-center gap-5 mt-2 mb-1 text-[11px] text-[var(--text-tertiary,#9ca3af)]">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-4 h-3 rounded-sm" style={{ backgroundColor: '#92400e', opacity: 0.85 }} />
          W1/W2 (Pooled)
        </span>
        <span className="flex items-center gap-1.5">
          <svg width="16" height="12" className="inline-block rounded-sm overflow-hidden">
            <defs>
              <pattern id="legend-stripe" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <rect width="6" height="6" fill="#92400e" opacity="0.4" />
                <line x1="0" y1="0" x2="0" y2="6" stroke="#92400e" strokeWidth="2" opacity="0.7" />
              </pattern>
            </defs>
            <rect width="16" height="12" fill="url(#legend-stripe)" />
          </svg>
          W3/Bilateral
        </span>
      </div>

      {/* Bottom section: IA heatmap + AoW traffic lights */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        {/* Impact Area Heatmap */}
        <div className="bg-[var(--surface-secondary,#f8fafc)] rounded-xl p-4 border border-[var(--border-primary,#e5e7eb)]">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary,#6b7280)] mb-3">Impact Area Targeting</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr>
                  <th className="text-left py-1 pr-2 text-[var(--text-tertiary,#9ca3af)] font-normal"></th>
                  {iaData.map(ia => (
                    <th key={ia.key} className="text-center py-1 px-1 font-semibold" style={{ color: ia.color }}>{ia.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'Principal', getVal: d => d.principal, opacity: 1 },
                  { label: 'Significant', getVal: d => d.significant, opacity: 0.5 },
                  { label: 'Not Targeted', getVal: d => d.notTargeted, opacity: 0.15 },
                ].map(row => (
                  <tr key={row.label}>
                    <td className="py-1 pr-2 text-[var(--text-tertiary,#9ca3af)]">{row.label}</td>
                    {iaData.map(ia => {
                      const val = row.getVal(ia);
                      const maxVal = Math.max(ia.principal, ia.significant, ia.notTargeted, 1);
                      const intensity = val / maxVal;
                      return (
                        <td key={ia.key} className="text-center py-1 px-1">
                          <div
                            className="mx-auto rounded w-full py-1 text-[10px] font-semibold tabular-nums"
                            style={{
                              backgroundColor: `${ia.color}${Math.round((0.15 + intensity * 0.55) * 255).toString(16).padStart(2, '0')}`,
                              color: intensity > 0.35 ? '#fff' : 'var(--text-primary, #374151)',
                              textShadow: intensity > 0.35 ? '0 0 2px rgba(0,0,0,0.3)' : 'none',
                            }}
                          >
                            {fmt(val)}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex gap-3 mt-2 pt-2 border-t border-[var(--border-primary,#e5e7eb)] text-[10px] text-[var(--text-tertiary,#9ca3af)]">
            <span>2 = Principal</span>
            <span>1 = Significant</span>
            <span>0 = Not Targeted</span>
          </div>
        </div>

        {/* AoW Traffic Lights */}
        <div className="bg-[var(--surface-secondary,#f8fafc)] rounded-xl p-4 border border-[var(--border-primary,#e5e7eb)]">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary,#6b7280)] mb-3">Areas of Work Progress</h4>
          {areasOfWork.length > 0 ? (
            <div className="space-y-2">
              {areasOfWork.map(aow => (
                <div key={aow.id} className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: TRAFFIC_COLORS[aow.ratingKey] || '#9ca3af' }} />
                  <span className="text-xs text-[var(--text-primary,#111827)] flex-1 truncate">{aow.name}</span>
                  <span className="text-[10px] tabular-nums text-[var(--text-tertiary,#9ca3af)]">
                    {aow.achievementPct != null ? `${Math.round(aow.achievementPct)}%` : '—'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-[var(--text-tertiary,#9ca3af)] italic">Select a program to see AoW progress</p>
          )}
        </div>
      </div>
    </ChartWrapper>
  );
});
